export type CrmStaffPermissionOption = {
  id: number;
  label: string;
  path: string;
  description?: string;
};

export type CrmStaffPermissionGroup = {
  id: string;
  label: string;
  description: string;
  options: readonly CrmStaffPermissionOption[];
};

export const crmStaffPermissionGroups: readonly CrmStaffPermissionGroup[] = [
  { id: 'dashboard', label: 'Dashboard', description: 'Business overview and headline figures.', options: [
    { id: 1, label: 'Dashboard', path: '/' }
  ]},
  { id: 'activities', label: 'Activities', description: 'Activity setup and category maintenance.', options: [
    { id: 2, label: 'Activities list', path: '/activities' },
    { id: 3, label: 'Activity categories', path: '/activity-categories' }
  ]},
  { id: 'locations', label: 'Work locations', description: 'Venue and work-location records.', options: [
    { id: 4, label: 'Work locations', path: '/locations' }
  ]},
  { id: 'products', label: 'Products', description: 'Product catalogue, categories and inventory.', options: [
    { id: 5, label: 'Stock', path: '/stock/items' },
    { id: 6, label: 'Stock categories', path: '/stock-categories' },
    { id: 7, label: 'Manage inventory', path: '/stock/inventory' }
  ]},
  { id: 'orders', label: 'Order management', description: 'Bookings, orders and their detail screens.', options: [
    { id: 8, label: 'Order management', path: '/orders' },
    { id: 34, label: 'Reschedule missed activity sessions', path: '/orders/reschedule-missed' }
  ]},
  { id: 'customers', label: 'Customers', description: 'Choose each customer workspace separately.', options: [
    { id: 9, label: 'All customers', path: '/customers/all' },
    { id: 10, label: 'Loyalty', path: '/customers/loyalty' },
    { id: 11, label: 'Membership', path: '/customers/membership' },
    { id: 12, label: 'Marketing', path: '/customers/marketing' },
    { id: 13, label: 'Special events', path: '/customers/special-events' }
  ]},
  { id: 'finance', label: 'Payments and finance', description: 'Payment history, refunds and invoices.', options: [
    { id: 14, label: 'Payment history', path: '/payments/history' },
    { id: 15, label: 'Refunds', path: '/refunds' },
    { id: 16, label: 'Invoices', path: '/invoices' }
  ]},
  { id: 'waivers', label: 'Waivers', description: 'Waiver rules and signed records.', options: [
    { id: 17, label: 'Waiver settings', path: '/waiver-settings' },
    { id: 18, label: 'Waiver management', path: '/waiver-management' }
  ]},
  { id: 'reviews', label: 'Reviews', description: 'Customer review records.', options: [
    { id: 19, label: 'Reviews', path: '/reviews' }
  ]},
  { id: 'operations', label: 'Venue operations', description: 'Operational screens are granted independently.', options: [
    { id: 20, label: 'POS', path: '/pos' },
    { id: 35, label: 'Block and unblock activity bookings', path: '/pos/block-activities', description: 'Block a full day or individual session across widget, kiosk and POS.' },
    { id: 21, label: 'Table management', path: '/tables' },
    { id: 22, label: 'Customer self-service kiosk', path: '/kiosk/self-service' },
    { id: 23, label: 'Kitchen display (KDS)', path: '/kds' }
  ]},
  { id: 'reporting', label: 'Reporting and configuration', description: 'Sensitive reporting and connection tools.', options: [
    { id: 24, label: 'Reports', path: '/reports' },
    { id: 25, label: 'Payment gateway details', path: '/payment-gateway' },
    { id: 26, label: 'Printer log', path: '/printer-log' }
  ]},
  { id: 'settings', label: 'Settings tabs', description: 'Only selected tabs appear inside Settings.', options: [
    { id: 27, label: 'General settings', path: '/settings/general' },
    { id: 28, label: 'Device connector', path: '/settings/devices' },
    { id: 29, label: 'System conditions', path: '/settings/conditions' },
    { id: 30, label: 'Legal documents', path: '/settings/legal' },
    { id: 31, label: 'Security', path: '/settings/security' },
    { id: 32, label: 'SMTP details', path: '/settings/smtp' },
    { id: 33, label: 'Scheduled jobs', path: '/settings/jobs' }
  ]}
] as const;

export const crmStaffPermissionOptions = crmStaffPermissionGroups.flatMap(group => group.options);

const legacyPermissionAliases: Readonly<Record<string, readonly string[]>> = {
  '/stock/items': ['/stock'],
  '/stock/inventory': ['/stock'],
  '/customers/all': ['/customers'],
  '/customers/loyalty': ['/customers'],
  '/customers/membership': ['/customers'],
  '/customers/marketing': ['/customers'],
  '/customers/special-events': ['/customers'],
  '/payments/history': ['/payments'],
  '/refunds': ['/payments'],
  '/invoices': ['/invoice'],
  '/kiosk/self-service': ['/kiosk'],
  '/kds': ['/kiosk'],
  '/settings/general': ['/settings'],
  '/settings/devices': ['/settings'],
  '/settings/conditions': ['/settings'],
  '/settings/legal': ['/settings'],
  '/settings/security': [],
  '/settings/smtp': [],
  '/settings/jobs': []
};

export function hasCrmStaffPermission(permissions: readonly string[], required: string) {
  if((required==='/orders/reschedule-missed' || required==='/pos/block-activities'))return permissions.includes(required);
  if (permissions.includes('*') || permissions.includes(required)) return true;
  return (legacyPermissionAliases[required] ?? []).some(permission => permissions.includes(permission));
}

export function hasAnySettingsPermission(permissions: readonly string[]) {
  return crmStaffPermissionGroups.find(group => group.id === 'settings')!.options
    .some(option => hasCrmStaffPermission(permissions, option.path));
}

export const superAdminStaffPermissionOptions = [
  { id: 1, label: 'Dashboard', path: '/' },
  { id: 2, label: 'CRM Admins', path: '/admin/clients' },
  { id: 3, label: 'CRM subscriptions', path: '/admin/subscriptions' },
  { id: 4, label: 'Inqueries', path: '/admin/inquiries' },
  { id: 5, label: 'Invoices', path: '/admin/invoices' },
  { id: 6, label: 'Email Templates', path: '/admin/email-templates' },
  { id: 7, label: 'Website', path: '/admin/website-settings' },
  { id: 8, label: 'Payments', path: '/admin/payments' },
  { id: 9, label: 'Marketing-CMS', path: '/admin/pages' },
  { id: 10, label: 'Admin Charges', path: '/admin/charge' },
  { id: 11, label: 'Platform AI', path: '/admin/ai-settings' }
] as const;

export function expandPermissionPaths(
  permissions: string[],
  options: readonly { path: string }[]
) {
  if (permissions.includes('*')) return options.filter(option=>!['/orders/reschedule-missed','/pos/block-activities'].includes(option.path) || permissions.includes(option.path)).map(option => option.path);
  const expanded = new Set(permissions);
  for (const option of options) {
    if (hasCrmStaffPermission(permissions, option.path)) expanded.add(option.path);
  }
  return [...expanded].filter(permission => options.some(option => option.path === permission));
}
